import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Play, CurrencyDollar, PencilSimple, Check } from "@phosphor-icons/react";
import { runBatch, setBudget as setBudgetApi } from "@/lib/api";
import { toast } from "sonner";

export default function BatchRunner({ storyId, story, shots, selectedIds, onSelectAll, onBatchStarted, onBudgetUpdated }) {
  const [starting, setStarting] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState(story.xai_credit_budget ?? "");

  useEffect(() => {
    setBudgetInput(story.xai_credit_budget ?? "");
  }, [story.xai_credit_budget]);

  const passed = shots.filter((s) => s.gen_status === "passed").length;
  const failedRed = shots.filter((s) => s.gen_status === "failed_red").length;
  const detour = shots.filter((s) => s.gen_status === "detour_bin").length;
  const totalCost = shots.reduce((sum, s) => sum + (s.total_cost || 0), 0);
  const budget = story.xai_credit_budget;
  const remaining = budget != null ? budget - totalCost : null;

  const handleRun = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one shot to run");
      return;
    }
    setStarting(true);
    try {
      const res = await runBatch(storyId, selectedIds);
      toast.success(`Batch started — ${res.shot_count} shot(s) queued (Grok + Warden QC)`);
      onBatchStarted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start batch");
    } finally {
      setStarting(false);
    }
  };

  const saveBudget = async () => {
    const amount = budgetInput === "" ? null : parseFloat(budgetInput);
    try {
      const updated = await setBudgetApi(storyId, amount);
      toast.success(amount ? `xAI budget set to $${amount}` : "Budget cleared");
      setEditingBudget(false);
      onBudgetUpdated(updated);
    } catch (e) {
      toast.error("Failed to save budget");
    }
  };

  return (
    <div data-testid="batch-runner-bar" className="border border-slate-800 rounded-sm p-3 bg-slate-900/50 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-heading uppercase tracking-wider text-xs font-semibold text-slate-400">
          Stage 3/4 — Batch Generation (Grok + The Warden)
        </h3>
        <div className="flex items-center gap-3 font-mono text-[11px] text-slate-500">
          <span className="text-emerald-400">{passed} passed</span>
          <span className="text-rose-500">{failedRed} red</span>
          <span className="text-fuchsia-400">{detour} detour</span>
        </div>
      </div>

      <div className="flex items-center justify-between bg-slate-950/60 border border-slate-800 rounded-sm px-3 py-2">
        <div className="flex items-center gap-4 font-mono text-xs">
          <span className="flex items-center gap-1 text-slate-300">
            <CurrencyDollar size={12} /> Tracked spend: <span className="text-slate-100 font-semibold">${totalCost.toFixed(3)}</span>
          </span>
          {editingBudget ? (
            <span className="flex items-center gap-1">
              <span className="text-slate-500">Budget: $</span>
              <Input
                data-testid="xai-budget-input"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                className="h-6 w-20 bg-slate-950 border-slate-800 rounded-sm text-xs px-1.5"
                placeholder="50"
              />
              <button data-testid="xai-budget-save-btn" onClick={saveBudget} className="text-emerald-400 hover:text-emerald-300">
                <Check size={14} />
              </button>
            </span>
          ) : budget != null ? (
            <>
              <span className="text-slate-500">Budget: <span className="text-slate-300">${budget.toFixed(2)}</span></span>
              <span className={remaining < 0 ? "text-rose-500" : "text-emerald-400"}>
                Est. remaining: ${remaining.toFixed(3)}
              </span>
              <button data-testid="xai-budget-edit-btn" onClick={() => setEditingBudget(true)} className="text-slate-600 hover:text-blue-400">
                <PencilSimple size={12} />
              </button>
            </>
          ) : (
            <button data-testid="xai-budget-set-btn" onClick={() => setEditingBudget(true)} className="text-slate-600 hover:text-blue-400 flex items-center gap-1">
              <PencilSimple size={12} /> Set budget for live remaining estimate
            </button>
          )}
        </div>
        <span className="text-[10px] text-slate-600 italic">xAI has no balance API — this is spend we've tracked, not a live console read</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button data-testid="batch-select-all-btn" onClick={onSelectAll} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
            {selectedIds.length === shots.length ? "Deselect All" : "Select All"}
          </button>
          <span className="text-xs text-slate-600">{selectedIds.length} selected</span>
        </div>
        <Button
          data-testid="run-batch-btn"
          onClick={handleRun}
          disabled={starting || selectedIds.length === 0}
          className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm"
        >
          <Play size={14} className="mr-2" />
          {starting ? "Starting…" : `Run Batch (${selectedIds.length})`}
        </Button>
      </div>
    </div>
  );
}
